import PropTypes from 'prop-types';
import { Star, Clock, Scissors } from 'react-feather';
import Text from 'atoms/Text';
import Tooltip from 'organisms/Tooltip';
import TruncatedSubtitle from 'molecules/TruncatedSubtitle';
import { toRailLabel } from 'lib/commandFormatRegistry';
import { IRS_RAIL_BUTTON_LIST } from 'lib/constants';
import { formatTime } from 'helper/dateGrouping';

// The tile shows the same icon as the command's LHS rail button. UNWIND is
// captured into history but has no rail button, so it is mapped here rather
// than by adding it to the rail list.
const ICON_BY_LABEL = {
    ...IRS_RAIL_BUTTON_LIST
        .flatMap((section) => section.items)
        .reduce((acc, item) => ({ ...acc, [item.name]: item.icon }), {}),
    UNWIND: Scissors
};

// A single Recent/Favourites tile: a green-tinted chip with the command's rail
// icon on the left; Line 1 = rail command label with the favourite star at the
// right, Line 2 = the structure from the shared registry; the execution
// timestamp sits at the bottom-right (Figma 63293-230397). Clicking the body
// reopens; clicking the star toggles favourite (star click takes precedence,
// Story §7).
const CommandHistoryTile = ({
    entry, isFavourite, onReopen, onToggleFavourite
}) => {
    const label = toRailLabel(entry.command);
    const CommandIcon = ICON_BY_LABEL[label];

    return (
        <div
            role="button"
            tabIndex={0}
            onClick={() => onReopen(entry)}
            className="bg-gray-800 rounded p-2 border-l-2 border-transparent hover:bg-success-500/15 hover:border-success-500 cursor-pointer"
        >
            <div className="flex gap-2">
                {CommandIcon && (
                    <div
                        data-testid="command-icon"
                        className="w-10 h-10 shrink-0 rounded bg-success-500/15 flex items-center justify-center"
                    >
                        <CommandIcon className="w-4 h-4 text-gray-100" />
                    </div>
                )}

                <div className="flex-1 min-w-0">
                    <div className="flex justify-between items-start gap-3">
                        <Text
                            variant="bodySmall"
                            fontWeight="font-semibold"
                            className="text-gray-100"
                        >
                            {label}
                        </Text>

                        <Tooltip
                            content={isFavourite ? 'Remove from Favourites' : 'Add to Favourites'}
                            position="top"
                        >
                            <button
                                type="button"
                                data-testid="favourite-star"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onToggleFavourite(entry);
                                }}
                                className="shrink-0"
                            >
                                <Star
                                    size={16}
                                    className={isFavourite ? 'text-success-500' : 'text-gray-200 hover:text-success-500'}
                                    fill={isFavourite ? 'currentColor' : 'none'}
                                />
                            </button>
                        </Tooltip>
                    </div>

                    <TruncatedSubtitle text={entry.line2 || ''} className="text-gray-200 mt-1" />
                </div>
            </div>

            <div className="flex items-center justify-end gap-1 mt-1">
                <Clock size={13} className="text-gray-200" />
                <Text variant="small" className="text-gray-200">
                    {formatTime(entry.executedAt)}
                </Text>
            </div>
        </div>
    );
};

CommandHistoryTile.propTypes = {
    entry: PropTypes.shape({
        id: PropTypes.string,
        command: PropTypes.string,
        line2: PropTypes.string,
        executedAt: PropTypes.string
    }).isRequired,
    isFavourite: PropTypes.bool,
    onReopen: PropTypes.func.isRequired,
    onToggleFavourite: PropTypes.func.isRequired
};

CommandHistoryTile.defaultProps = {
    isFavourite: false
};

export default CommandHistoryTile;
